import React, { useCallback, useEffect, useState } from 'react'
import { App, Button, Form, Modal, Space, Table, Tag } from 'antd'
import type { TableColumnsType } from 'antd'
import { EditOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    PaginatedListCard,
    PaginatedListContainer,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../../../components/common/PaginatedListTable'
import { useDedupedMessage } from '../../../hooks/useDedupedMessage'
import TypeColorSelect from '../../../components/common/TypeColorSelect'
import { RESERVED_TYPE_COLOR } from '../../../components/common/typeColorSelectConfig'
import {
    getModelRawClassOptions,
    updateModelRecognitionTypeColor,
} from '../../../services/modelManagementService'
import type {
    ModelRawClassVersionSourceSummary,
    ModelRecognitionTypeSummary,
} from '../../../types/modelManagement'
import { getRawClassLocalizedName } from '../../../utils/rawClassDisplay'
import { buildModelRecognitionTypes } from '../../../utils/modelRecognitionTypeOptions'
import {
    getModelManagementErrorDetail,
    MODEL_MANAGEMENT_I18N_PREFIX,
} from './pageConfig'

interface ColorFormValues {
    color?: string
}

interface ModelRecognitionTypesSectionProps {
    canUpdate: boolean
}

const ModelRecognitionTypesSection: React.FC<
    ModelRecognitionTypesSectionProps
> = ({ canUpdate }) => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t } = useTranslation(['admin'])
    const [form] = Form.useForm<ColorFormValues>()
    const [types, setTypes] = useState<ModelRecognitionTypeSummary[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [editingType, setEditingType] =
        useState<ModelRecognitionTypeSummary | null>(null)
    const [sourceVersionsType, setSourceVersionsType] =
        useState<ModelRecognitionTypeSummary | null>(null)

    const loadTypes = useCallback(async () => {
        setLoading(true)
        try {
            const options = await getModelRawClassOptions()
            setTypes(buildModelRecognitionTypes(options))
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadModelRecognitionTypesFailed`
                    ),
                    t
                )
            )
        } finally {
            setLoading(false)
        }
    }, [message, t])

    useEffect(() => {
        void loadTypes()
    }, [loadTypes])

    const openEditor = (record: ModelRecognitionTypeSummary) => {
        setEditingType(record)
        form.resetFields()
        form.setFieldsValue({ color: record.color })
    }

    const closeEditor = () => {
        setEditingType(null)
    }

    const formatTypeName = (record: ModelRecognitionTypeSummary): string => {
        if (record.display_name_zh) {
            return `${record.display_name_zh} (${record.raw_class})`
        }
        const localized = getRawClassLocalizedName(record.raw_class, t)
        return localized === record.raw_class
            ? record.raw_class
            : `${localized} (${record.raw_class})`
    }

    const saveColor = async () => {
        if (!editingType) return
        const values = await form.validateFields()
        if (!values.color) return
        setSaving(true)
        try {
            await updateModelRecognitionTypeColor(
                editingType.raw_class_norm,
                values.color.toLowerCase()
            )
            closeEditor()
            await loadTypes()
            message.success(
                t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.modelRecognitionTypeSaveSuccess`
                )
            )
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.modelRecognitionTypeSaveFailed`
                    ),
                    t
                )
            )
        } finally {
            setSaving(false)
        }
    }

    const columns: TableColumnsType<ModelRecognitionTypeSummary> = [
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.columns.model`
            ),
            dataIndex: 'model_keys',
            key: 'model',
            width: 120,
            render: (modelKeys: string[]) => (
                <Space size={[4, 4]} wrap>
                    {modelKeys.map((modelKey) => (
                        <Tag key={modelKey}>
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelKeys.${modelKey}`,
                                { defaultValue: modelKey },
                            )}
                        </Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.columns.name`
            ),
            dataIndex: 'raw_class',
            key: 'name',
            width: 180,
            render: (_rawClass: string, record) => formatTypeName(record),
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.columns.key`
            ),
            dataIndex: 'raw_class_norm',
            key: 'key',
            width: 200,
            render: renderPaginatedListText,
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.columns.color`
            ),
            dataIndex: 'color',
            key: 'color',
            width: 100,
            render: (color: string) => (
                <span
                    aria-label={color}
                    style={{
                        background: color,
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        borderRadius: 4,
                        display: 'inline-block',
                        height: 20,
                        verticalAlign: 'middle',
                        width: 32,
                    }}
                />
            ),
        },
        {
            title: t(
                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.columns.actions`
            ),
            key: 'actions',
            fixed: 'right',
            width: 220,
            render: (_value, record) => (
                <Space size={4}>
                    <Button
                        icon={<UnorderedListOutlined />}
                        onClick={() => setSourceVersionsType(record)}
                        type="link"
                    >
                        {t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.sourceVersions`
                        )}
                    </Button>
                    {canUpdate ? (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => openEditor(record)}
                            type="link"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.editColor`
                            )}
                        </Button>
                    ) : null}
                </Space>
            ),
        },
    ]

    return (
        <section>
            <PaginatedListContainer>
                <PaginatedListCard>
                    <Table
                        columns={columns}
                        dataSource={types}
                        loading={loading}
                        pagination={buildPaginatedListPagination({
                            defaultPageSize: 10,
                            showTotal: (total) =>
                                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                    total,
                                }),
                        })}
                        rowKey={(record) => record.raw_class_norm}
                        scroll={{ x: 860 }}
                        size={PAGINATED_LIST_TABLE_SIZE}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>

            <Modal
                confirmLoading={saving}
                destroyOnClose
                forceRender
                okText={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitCategory`
                )}
                onCancel={closeEditor}
                onOk={() => void saveColor()}
                open={editingType !== null}
                maskClosable={false}
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypeModal.title`
                )}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.modelRecognitionTypeColor`
                        )}
                        name="color"
                        rules={[
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorRequired`
                                ),
                                required: true,
                            },
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorInvalid`
                                ),
                                pattern: /^#[0-9A-Fa-f]{6}$/,
                            },
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorReserved`,
                                ),
                                validator: (_rule, value) =>
                                    value?.toLowerCase() === RESERVED_TYPE_COLOR
                                        ? Promise.reject()
                                        : Promise.resolve(),
                            },
                        ]}
                    >
                        <TypeColorSelect
                            aria-label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.form.modelRecognitionTypeColor`,
                            )}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                destroyOnClose
                footer={null}
                onCancel={() => setSourceVersionsType(null)}
                open={sourceVersionsType !== null}
                maskClosable={false}
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.sourceVersionsModal.title`,
                    {
                        type: sourceVersionsType
                            ? formatTypeName(sourceVersionsType)
                            : '',
                    }
                )}
                width={640}
            >
                <Table<ModelRawClassVersionSourceSummary>
                    columns={[
                        {
                            dataIndex: 'version_label',
                            key: 'version',
                            title: t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.sourceVersionsModal.version`
                            ),
                            render: renderPaginatedListText,
                        },
                    ]}
                    dataSource={sourceVersionsType?.source_versions ?? []}
                    locale={{
                        emptyText: t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.sourceVersionsModal.empty`
                        ),
                    }}
                    pagination={buildPaginatedListPagination({
                        defaultPageSize: 10,
                        showTotal: (total) =>
                            t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.modelRecognitionTypes.sourceVersionsModal.total`,
                                { total }
                            ),
                    })}
                    rowKey="model_version_id"
                    scroll={{ x: 420 }}
                    size={PAGINATED_LIST_TABLE_SIZE}
                    tableLayout="fixed"
                />
            </Modal>
        </section>
    )
}

export default ModelRecognitionTypesSection
